import React, { useCallback, useEffect, useState, cloneElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from '../ui/FadeIn';
import { CheckCircle2Icon, ZapIcon, TargetIcon, EyeIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/bGUnoikiDg6p8h9qJDnwC1/heylloyd-image-1772660592396.png',
  alt: 'Funciones Musculares - Agonistas, Antagonistas, Sinergistas'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2RHjb1rcN9VnXHxz8ubVPY/heylloyd-image-1772660520858.png',
  alt: 'Plancha Frontal - Ejecución y Errores Comunes'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/t6BzSebxutPAJbxWDsSGQQ/heylloyd-image-1772660266126.png',
  alt: 'Sentadilla Frontal - Ejecución y Análisis'
}];
export function SolutionSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const next = useCallback(() => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % carouselImages.length);
  }, []);
  const prev = useCallback(() => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + carouselImages.length) % carouselImages.length);
  }, []);
  const goTo = useCallback((index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  }, [current]);
  useEffect(() => {
    const timer = setInterval(next, 5000);
    return () => clearInterval(timer);
  }, [next]);
  const features = [{
    icon: <TargetIcon className="w-6 h-6 text-primary" data-id="element-301" />,
    title: 'Músculos Implicados',
    desc: 'Agonistas, sinergistas y estabilizadores detallados.',
    bg: 'bg-primary/5 border-primary/20',
    iconBg: 'bg-primary/10'
  }, {
    icon: <ZapIcon className="w-6 h-6 text-primary" data-id="element-302" />,
    title: 'Movimientos Articulares',
    desc: 'Análisis biomecánico para una ejecución perfecta.',
    bg: 'bg-primary/5 border-primary/20',
    iconBg: 'bg-primary/10'
  }, {
    icon: <EyeIcon className="w-6 h-6 text-primary" data-id="element-303" />,
    title: 'Errores Frecuentes',
    desc: 'Identificá y corregí fallos antes de que ocurran.',
    bg: 'bg-primary/5 border-primary/20',
    iconBg: 'bg-primary/10'
  }];
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0
    })
  };
  return <section className="py-6 relative overflow-hidden bg-background" data-id="element-304">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" data-id="element-305" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[100px] pointer-events-none" data-id="element-306" />

      <div className="container mx-auto px-4 max-w-4xl relative z-10" data-id="element-307">
        <FadeIn className="text-center mb-5" data-id="element-308">
          <span className="text-primary font-bold tracking-widest uppercase text-xs mb-2 block" data-id="element-309">
            El Mecanismo Único
          </span>
          <h2 className="text-2xl md:text-3xl font-black mb-3 text-gray-900" data-id="element-310">
            La Base Técnica de{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary" data-id="element-311">
              140 Ejercicios
            </span>{' '}
            Analizados
          </h2>
          <p className="text-base text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-312">
            No es una lista de ejercicios: es un{' '}
            <span className="text-gray-900 font-semibold" data-id="element-313">sistema</span>. Cada
            uno está desglosado (músculos, articulaciones, técnica, errores e
            ilustraciones) para que respondas con{' '}
            <span className="text-gray-900 font-semibold" data-id="element-314">seguridad</span>:{' '}
            <strong className="text-primary font-semibold" data-id="element-315">
              "¿por qué este ejercicio?"
            </strong>
          </p>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-3 mb-6" data-id="element-316">
          {features.map((feature, index) => <FadeIn key={index} delay={0.1 + index * 0.1} direction="up" data-id="element-317">
              <div className={`${feature.bg} border rounded-lg p-3 h-full hover:shadow-md transition-all group`} data-id="element-318">
                <div className="flex items-center gap-2 mb-1" data-id="element-319">
                  <div className={`w-8 h-8 rounded-lg ${feature.iconBg} flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform`} data-id="element-320">
                    {cloneElement(feature.icon, {
                  className: 'w-4 h-4 text-primary'
                })}
                  </div>
                  <h3 className="text-sm font-bold text-gray-900" data-id="element-321">
                    {feature.title}
                  </h3>
                </div>
                <p className="text-gray-500 text-xs" data-id="element-322">{feature.desc}</p>
              </div>
            </FadeIn>)}
        </div>

        <FadeIn delay={0.3} data-id="element-323">
          <div className="bg-gradient-to-r from-gray-100 to-gray-200 border border-card-border rounded-xl p-4 md:p-6 flex flex-col lg:flex-row items-center gap-6 shadow-xl shadow-primary/5" data-id="element-324">
            <div className="flex-1" data-id="element-325">
              <h3 className="text-lg font-bold mb-2 text-gray-900" data-id="element-326">
                Cada ejercicio está desglosado visualmente
              </h3>
              <p className="text-gray-500 text-sm mb-4" data-id="element-327">
                Ilustraciones claras y ejecución correcta paso a paso. Diseñado
                para consulta rápida en el gimnasio o antes de tus clases.
              </p>
              <ul className="space-y-2" data-id="element-328">
                {['Ilustraciones anatómicas claras', 'Checklists de ejecución rápida', 'Formato digital accesible 24/7'].map((item, i) => <li key={i} className="flex items-center gap-2 text-gray-600 text-sm" data-id="element-329">
                    <CheckCircle2Icon className="w-4 h-4 text-green-500 shrink-0" data-id="element-330" />
                    <span data-id="element-331">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Image Carousel */}
            <div className="w-full lg:w-2/5 shrink-0" data-id="element-332">
              <div className="relative overflow-hidden rounded-lg border border-gray-300 bg-gray-800 aspect-[3/4]" data-id="element-333">
                <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-334">
                  <motion.img key={current} src={carouselImages[current].src} alt={carouselImages[current].alt} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
                  duration: 0.4,
                  ease: 'easeInOut'
                }} className="absolute inset-0 w-full h-full object-contain p-2" data-id="element-335" />
                </AnimatePresence>

                {/* Navigation arrows */}
                <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-white hover:bg-primary/80 transition-colors z-30" aria-label="Imagen anterior" data-id="element-336">
                  <ChevronLeftIcon className="w-5 h-5" data-id="element-337" />
                </button>
                <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/60 border border-white/10 flex items-center justify-center text-white hover:bg-primary/80 transition-colors z-30" aria-label="Imagen siguiente" data-id="element-338">
                  <ChevronRightIcon className="w-5 h-5" data-id="element-339" />
                </button>
              </div>

              {/* Dot indicators */}
              <div className="flex justify-center gap-2 mt-3" data-id="element-340">
                {carouselImages.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === current ? 'bg-primary w-4' : 'bg-gray-400 hover:bg-gray-600'}`} aria-label={`Ir a imagen ${index + 1}`} data-id="element-341" />)}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
}